import React, { useEffect, useState } from "react";
import analytics from "web/script/analytics/analytics";
import classnames from "classnames";
import { useSmallBreakpoint } from "web/react/hooks/use-breakpoint/use-breakpoint";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import TrackedButton from "web/react/components/tracked-button/tracked-button";

import { gettext } from "web/script/modules/django-i18n";
// eslint-disable-next-line css-modules/no-unused-class
import styles from "./brand-ad-container.css";

type InfoButtonType = "light" | "dark";

const STYLE_TYPE_CLASSES: { readonly [bt in InfoButtonType]: { icon: string; label: string } } = {
    light: { icon: styles.lightInfoIcon, label: styles.lightInfoLabel },
    dark: { icon: styles.darkInfoIcon, label: styles.darkInfoLabel },
};

interface InfoButtonProps {
    partnerSlug: string;
    designerSlug: undefined | string;
    styleType: string;
    labelText: string;
}

function InfoButton({
    partnerSlug,
    designerSlug,
    styleType,
    labelText,
}: InfoButtonProps): JSX.Element {
    const [isActive, setIsActive] = useState(false);

    const iconClass = STYLE_TYPE_CLASSES[styleType]["icon"];
    const labelClass = STYLE_TYPE_CLASSES[styleType]["label"];
    if (!iconClass || !labelClass) {
        throw new Error(`Unknown type ${styleType}!`);
    }

    function toggleInfo(): void {
        analytics.event("brand_ads", "click", "perf_brand_ad_icon", false, {
            partner: partnerSlug,
            designer_slug: designerSlug,
        });
        setIsActive(!isActive);
    }

    return (
        <button
            className={classnames(styles.info, {
                [styles.active]: isActive,
            })}
            onClick={toggleInfo}
        >
            <span className={classnames(styles.infoIcon, iconClass)}>{"i"}</span>
            <span className={classnames(styles.infoLabel, labelClass)}>{labelText}</span>
        </button>
    );
}

export interface BrandAdContainerProps {
    webp_banner_image: string;
    jpg_banner_image: string;
    link: string;
    mobile_link: string;
    webp_mpu_image: string;
    jpg_mpu_image: string;
    product_image: string;
    product_link: string;
    product_description: string;
    product_price: string;
    partner: string;
    designer_slug?: string;
    has_margin?: boolean;
}

function BrandAdContainer({
    webp_banner_image: webpBannerImage,
    jpg_banner_image: jpgBannerImage,
    link,
    mobile_link: mobileLink,
    webp_mpu_image: webpMpuImage,
    jpg_mpu_image: jpgMpuImage,
    product_image: productImage,
    product_link: productLink,
    product_description: productDescription,
    product_price: productPrice,
    partner,
    designer_slug: designerSlug,
    has_margin: hasMargin,
}: BrandAdContainerProps): null | JSX.Element {
    const partnerSlug = partner.toLowerCase().replace(/\s+/g, "-");

    useEffect(() => {
        analytics.event("brand_ads", "impression", "perf_brand_ad", true, {
            partner: partnerSlug,
            designer_slug: designerSlug,
        });
    }, [partnerSlug, designerSlug]);

    const partnerLabel = (
        <>
            <SVGIcon name="hyperlink" />
            <span className={styles.textWithIcon}>{partner}</span>
        </>
    );

    const partnerProductButton = (
        <TrackedButton
            className={styles.partnerProductLink}
            href={productLink}
            eventCategory="brand_ads"
            eventAction="click"
            eventLabel="perf_brand_product_image"
            openInNewTab={true}
            eventCustomData={{ partner: partnerSlug, designer_slug: designerSlug }}
        >
            <img
                className={classnames(styles.containerImage, styles.partnerProductImage)}
                src={productImage}
                alt={productDescription}
            />
            <div className={styles.productDetailsContainer}>
                <div className={styles.productDetails}>{productDescription}</div>
                {productPrice && (
                    <div className={styles.productPrice}>
                        <SVGIcon name="truck" />
                        <span className={styles.textWithIcon}>{productPrice}</span>
                    </div>
                )}
                <div className={styles.productPartnerLabel}>{partnerLabel}</div>
            </div>
        </TrackedButton>
    );

    const mainButtonHref = useSmallBreakpoint() ? link : mobileLink;

    const mainButton = (
        <TrackedButton
            href={mainButtonHref}
            eventCategory="brand_ads"
            eventAction="click"
            eventLabel="perf_brand_ad_banner"
            openInNewTab={true}
            eventCustomData={{ partner: partnerSlug, designer_slug: designerSlug }}
        >
            <picture>
                <source media="(min-width: 576px)" srcSet={webpBannerImage} type="image/webp" />
                <source media="(max-width: 575px)" srcSet={webpMpuImage} type="image/webp" />
                <source media="(min-width: 576px)" srcSet={jpgBannerImage} type="image/jpeg" />
                <source media="(max-width: 575px)" srcSet={jpgMpuImage} type="image/jpeg" />
                <img className={styles.containerImage} src={jpgBannerImage} alt="test banner" />
            </picture>
            <div className={styles.bannerPartnerLabel}>{partnerLabel}</div>
        </TrackedButton>
    );

    return (
        <div
            className={classnames(styles.container, {
                [styles.hasMargin]: hasMargin,
            })}
        >
            <div className={styles.partnerProductContainer}>
                {partnerProductButton}
                <InfoButton
                    partnerSlug={partnerSlug}
                    designerSlug={designerSlug}
                    styleType="light"
                    labelText={gettext("feed.header.featured_label")} // Featured
                />
            </div>
            <div className={styles.bannerContainer}>
                {mainButton}
                <InfoButton
                    partnerSlug={partnerSlug}
                    designerSlug={designerSlug}
                    styleType="dark"
                    labelText={gettext("feed.header.sponsored_label")} // Sponsored
                />
            </div>
        </div>
    );
}

export default BrandAdContainer;
